/**
 * Nurse Discharge Visit Note — static options + empty form state.
 * Sections mirror Eval & Orders → Nurse Discharge Note screenshots.
 */

import { emptyNurseNotesCommonHeader } from "./nurseNotesCommonHeaderUtils";
import { emptyWoundAssessmentValues } from "./woundAssessmentContent";

export const GENDER_OPTIONS = ["Male", "Female", "Other", "Unknown"];

export const DISCHARGE_REASON_OPTIONS = [
  "Skilled Nursing Goals Met",
  "Patient Stable / No Longer Requires Skilled Care",
  "Medication Management Completed",
  "Wound Care Goals Met",
  "Disease Management Education Completed",
  "Physician Discontinued Services",
  "Patient Declined Further Services",
  "Patient Transferred to Facility",
  "Hospitalization",
  "Insurance/Authorization Ended",
  "Non-Compliance",
  "Patient Deceased",
];

export const SKIN_INTEGRITY_OPTIONS = ["Intact", "Impaired"];

export const YES_NO_OPTIONS = ["Yes", "No"];

export const MENTAL_STATUS_OPTIONS = [
  "Alert & Oriented x4",
  "Alert & Oriented x3",
  "Confused",
  "Forgetful",
  "Unable to Assess",
];

export const RESPIRATORY_OPTIONS = [
  "Clear",
  "Diminished",
  "Crackles",
  "Wheezing",
  "Other",
];

export const CARDIO_RHYTHM_OPTIONS = ["Regular", "Irregular"];

export const EDEMA_OPTIONS = ["None", "Present"];

export const APPETITE_OPTIONS = ["Good", "Fair", "Poor"];

export const GI_STATUS_OPTIONS = ["Normal", "Constipation", "Diarrhea", "Other"];

export const GU_STATUS_OPTIONS = ["Normal", "Incontinence", "Catheter", "Other"];

export const DISEASE_CONDITION_OPTIONS = [
  "Diabetes Management",
  "Hypertension",
  "CHF Management",
  "COPD Management",
  "Infection Prevention",
  "Pain Management",
];

export const UNDERSTANDING_OPTIONS = [
  "Demonstrated Understanding",
  "Verbalized Understanding",
  "Needs Reinforcement",
];

export const MOBILITY_OPTIONS = [
  "Independent",
  "Requires Assistive Device",
  "Requires Assistance",
  "Dependent",
  "Bedbound",
];

export const FALL_RISK_OPTIONS = ["Low", "Moderate", "High"];

export const FALL_EDUCATION_OPTIONS = ["Completed", "Not Required"];

export const SN_GOAL_ROWS = [
  "Medication Management",
  "Disease Education",
  "Safety Goals",
  "Wound Care Goals",
  "Patient/Caregiver Training",
];

export const GOAL_STATUS_OPTIONS = ["Met", "Partially Met", "Not Met"];

export const EDUCATION_TOPIC_OPTIONS = [
  "Medication Management",
  "Disease Process",
  "Diet/Nutrition",
  "Fall Prevention",
  "Infection Prevention",
  "Emergency Plan",
  "Signs/Symptoms to Report",
  "Follow-Up Care",
];

export const LEARNER_OPTIONS = ["Patient", "Family/Caregiver"];

export const DISCHARGED_TO_OPTIONS = [
  "Home Independent",
  "Home With Family/Caregiver Support",
  "Outpatient Follow-Up",
  "Physician Management",
  "Facility Transfer",
];

export const FOLLOW_UP_OPTIONS = [
  "Follow Up With PCP",
  "Continue Medication Plan",
  "Continue Physician Orders",
  "Continue Home Exercise Program",
  "Continue Wound Care Plan (if applicable)",
];

export const YES_NO_NA_OPTIONS = ["Yes", "No", "N/A"];

export const toggleInList = (list, item) => {
  const arr = Array.isArray(list) ? list : [];
  return arr.includes(item) ? arr.filter((x) => x !== item) : [...arr, item];
};

export const newMedicationRowId = () =>
  `med-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

export const emptyMedicationRow = () => ({
  id: newMedicationRowId(),
  medicationName: "",
  dosageRoute: "",
  frequency: "",
  reconciled: false,
});

export const mapPatientMedicationsToDischargeRows = (medications) => {
  const list = Array.isArray(medications) ? medications : [];
  const active = list.filter(
    (med) => !med?.status || String(med.status).toLowerCase() === "active",
  );
  const source = active.length ? active : list;
  return source.map((med) => {
    const dose = med.dosage || med.dose || "";
    const route = med.route || "";
    return {
      id: med.id != null ? `patient-med-${med.id}` : newMedicationRowId(),
      medicationName: med.medicationName || "",
      dosageRoute: [dose, route].filter(Boolean).join(" ").trim() || dose,
      frequency: med.frequency || "",
      reconciled: false,
      sourceMedicationId: med.id ?? null,
    };
  });
};

export const emptyGoalStatuses = () =>
  Object.fromEntries(SN_GOAL_ROWS.map((goal) => [goal, ""]));

/** Full editable Nurse Discharge payload (JSON on dedicated row). */
export const emptyRnDischargePayload = () => ({
  billingCode: "",
  timeIn: "",
  timeOut: "",

  commonHeader: emptyNurseNotesCommonHeader(),

  /** Episode extras (demographics basics live in commonHeader). */
  primaryLanguage: "",
  socDate: "",
  dischargeDate: "",

  /** Legacy snapshot fields retained for older payloads / common-header seed. */
  fullName: "",
  mrn: "",
  dateOfBirth: "",
  gender: "",
  phone: "",
  homeAddress: "",

  dischargeReasons: [],
  dischargeReasonOther: "",

  vitalsBp: "",
  vitalsHr: "",
  vitalsRr: "",
  vitalsTemp: "",
  vitalsSpo2: "",
  vitalsWeight: "",
  painLevel: null,

  skinIntegrity: [],
  ...emptyWoundAssessmentValues(),

  mentalStatus: [],
  mentalStatusComments: "",
  respiratoryFindings: [],
  respiratoryOther: "",
  respiratoryStatus: "",
  cardioRhythm: [],
  edema: [],
  edemaLocationSeverity: "",
  giAppetite: [],
  giStatus: [],
  giOther: "",
  giComments: "",
  guStatus: [],
  guOther: "",
  guComments: "",

  medications: [],
  finalMedListProvided: [],
  finalEducationProvided: [],

  diseaseConditions: [],
  diseaseConditionsOther: "",
  patientUnderstanding: [],

  mobility: "Independent",
  fallRisk: [],
  fallPreventionEducation: [],

  goalStatuses: emptyGoalStatuses(),
  goalSummary: "",

  educationTopics: [],
  learners: [],
  educationResponse: "",

  dischargedTo: [],
  dischargedToOther: "",
  followUpRecommendations: [],
  additionalInstructions: "",

  physicianNotified: "",
  rnCaseManagerNotified: "",
  therapyTeamNotified: "",
  communicationNotes: "",

  dischargeSummary: "",
});

export const today = () => new Date().toISOString().slice(0, 10);
